import React from 'react';
import { useLocation } from 'react-router-dom';
import ProductPage from '../components/seo/ProductPage';
import { isEnglishPath } from '../utils/i18nRoutes';

const ALTERNATES = [
  { hreflang: 'es', href: '/lixo' },
  { hreflang: 'en', href: '/en/lixo' },
  { hreflang: 'x-default', href: '/lixo' },
];

const CONTENT = {
  es: {
    seoTitle: 'Lixo | Inteligencia de residuos con IoT y Machine Learning | Valoriza',
    seoDescription: 'Sensores IoT y machine learning para monitorizar residuos en tiempo real y optimizar la recogida. Primer piloto ganador del Hub de Valoriza.',
    breadcrumbLabel: 'Lixo',
    tag: 'Tecnología en campo',
    titleMain: 'Lixo: inteligencia de',
    titleAccent: 'residuos',
    lead: 'Sensores IoT y machine learning para monitorizar residuos en tiempo real y optimizar la recogida. Piloto de referencia en el Hub de Valoriza.',
    problema: 'La gestión de residuos tradicional se basa en rutas fijas y tiempos de recogida preestablecidos, lo que puede llevar a desbordamientos de contenedores, ineficiencias en la ruta y un aumento de costes operativos. La falta de información en tiempo real impide una respuesta ágil a las necesidades de la ciudad.',
    comoFunciona: 'Lixo utiliza una combinación de sensores IoT instalados en los contenedores de residuos y algoritmos de machine learning para monitorizar los niveles de llenado en tiempo real. Estos datos permiten optimizar las rutas de recogida, priorizando los contenedores que necesitan ser vaciados, reduciendo así viajes innecesarios y asegurando que los contenedores no se desborden.',
    cifras: [{ value: '1.er', label: 'Piloto ganador' }, { value: '3×', label: 'Eficiencia de ruta' }],
    paraQuien: 'Ayuntamientos y gestores de servicios urbanos que buscan optimizar la recogida de residuos, reducir costes operativos y mejorar la limpieza y eficiencia de la ciudad. Equipos de operaciones que necesitan visibilidad en tiempo real para una toma de decisiones más inteligente.',
    stack: ['Sensores IoT', 'Machine Learning', 'Plataforma Cloud', 'Análisis de datos'],
    enlaces: [{ label: 'TrisTrash: visión artificial en calle', to: '/tristrash' }, { label: 'Contacta con nosotros', to: '/contacto' }],
    ctaText: '¿Tienes un reto parecido?',
  },
  en: {
    seoTitle: 'Lixo | Waste intelligence with IoT and Machine Learning | Valoriza',
    seoDescription: 'IoT sensors and machine learning to monitor waste in real time and optimize collection. First winning pilot at Valoriza\u2019s Innovation Hub.',
    breadcrumbLabel: 'Lixo',
    tag: 'Field technology',
    titleMain: 'Lixo: waste',
    titleAccent: 'intelligence',
    lead: 'IoT sensors and machine learning to monitor waste in real time and optimize collection. Reference pilot at Valoriza\u2019s Innovation Hub.',
    problema: 'Traditional waste management relies on fixed routes and preset collection times, which can lead to overflowing bins, inefficient routes and higher operating costs. The lack of real-time information prevents an agile response to the city\u2019s needs.',
    comoFunciona: 'Lixo combines IoT sensors installed in waste containers with machine learning algorithms to monitor fill levels in real time. This data optimizes collection routes, prioritizing containers that need to be emptied, reducing unnecessary trips and ensuring containers don\u2019t overflow.',
    cifras: [{ value: '1st', label: 'Winning pilot' }, { value: '3×', label: 'Route efficiency' }],
    paraQuien: 'City councils and urban services managers looking to optimize waste collection, reduce operating costs and improve city cleanliness and efficiency. Operations teams that need real-time visibility for smarter decision-making.',
    stack: ['IoT sensors', 'Machine learning', 'Cloud platform', 'Data analytics'],
    enlaces: [{ label: 'TrisTrash: computer vision on the street', to: '/en/tristrash' }, { label: 'Contact us', to: '/en/contact' }],
    ctaText: 'Do you have a similar challenge?',
  },
};

export default function LixoPage() {
  const { pathname } = useLocation();
  const locale = isEnglishPath(pathname) ? 'en' : 'es';
  const c = CONTENT[locale];
  const path = locale === 'en' ? '/en/lixo' : '/lixo';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Lixo',
    description: c.seoDescription,
    url: `https://innovation.valorizasm.com${path}`,
    brand: { '@id': 'https://innovation.valorizasm.com/#organization' },
  };

  return (
    <ProductPage
      testId="lixo-page"
      path={path}
      locale={locale}
      alternates={ALTERNATES}
      jsonLd={[jsonLd]}
      problemaLabel={locale === 'en' ? 'The problem' : 'El problema'}
      comoFuncionaLabel={locale === 'en' ? 'How it works' : 'Cómo funciona'}
      cifrasLabel={locale === 'en' ? 'Figures' : 'Cifras'}
      paraQuienLabel={locale === 'en' ? "Who it's for" : 'Para quién'}
      enlacesLabel={locale === 'en' ? 'Links' : 'Enlaces'}
      {...c}
    />
  );
}
